/**
 * advance-space-structure-maintenance.ts — per-tick maintenance drain
 * for Constructor-built space structures (v4M4.e.3).
 *
 * Each owned space structure with non-zero `maintenanceCredits` /
 * `maintenanceMinerals` in `structures.spaceByKind` deducts that
 * amount from its owner empire's treasury per tick. Drain is clamped
 * at 0 (no negative resources; consistent with ship-upkeep convention
 * elsewhere in the economy pass). No bankruptcy / despawn behaviour —
 * structures stay alive even when the empire can't fully afford
 * upkeep; the treasury just floors at 0. Defining a bankruptcy /
 * auto-disable mechanic is a future design call.
 *
 * Runs alongside the other per-tick economy passes (after
 * `advanceSpaceExtractors` so income lands before drain in the same
 * tick — the order is arithmetically neutral but matches the
 * "produce-then-consume" mental model).
 *
 * ODP Platform isn't in spaceByKind (synthetic chassis spawned by
 * `advance-odp.ts`, not Constructor-built) so the iteration naturally
 * skips it — no maintenance cost. Minefield rows currently author
 * maintenance = 0 (the chassis depletes via mineCount instead); if a
 * future xlsx pass sets non-zero maintenance the drain applies.
 *
 * Mid-jump (`systemId === null`) structures stay buggy / aren't
 * possible — space structures carry `STATIONARY_SPECIAL_ABILITY` and
 * never jump — but the defensive `systemId !== null` check matches
 * the extractors pass anyway.
 */

import type { Empire, GameState } from '../state/types/index';

export function advanceSpaceStructureMaintenance(state: GameState): void {
  const sd = state.structuresData;
  // Accumulate deltas per empire so we drain + clamp once at the end,
  // matching the per-empire pattern in advanceSpaceExtractors.
  const deltas = new Map<Empire, { credits: number; minerals: number }>();
  const getDelta = (empire: Empire): { credits: number; minerals: number } => {
    let d = deltas.get(empire);
    if (!d) {
      d = { credits: 0, minerals: 0 };
      deltas.set(empire, d);
    }
    return d;
  };

  for (const ship of state.ships.values()) {
    if (ship.currentHull <= 0) continue;
    if (ship.systemId === null) continue; // defensive; stationary kit can't jump
    const row = sd.spaceByKind[ship.chassis];
    if (!row) continue; // ODP Platform + non-space-structure chassis fall through
    if (row.maintenanceCredits <= 0 && row.maintenanceMinerals <= 0) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const d = getDelta(empire);
    d.credits += row.maintenanceCredits;
    d.minerals += row.maintenanceMinerals;
  }

  for (const [empire, d] of deltas) {
    if (d.credits > 0) {
      empire.resources.credits = Math.max(0, empire.resources.credits - d.credits);
    }
    if (d.minerals > 0) {
      empire.resources.minerals = Math.max(
        0,
        empire.resources.minerals - d.minerals,
      );
    }
  }
}
